import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { CollapsiblePanel } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel';
import { CollapsiblePanelProvider } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel.context';
import { FinanceSelect } from '../../../../financeProviders/shared/FinanceSelect';
import { Label } from '../../../../../Toolkit/Inputs/Label';
import { Spacer } from '../../../../../../helpers/genericStyles';
import { Divider } from '../../../../../ToolkitV2/Divider/Divider';
import { FinancePriceField } from '../../../../financeProviders/shared/PriceField/PriceField';
import { FinanceTextField } from '../../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { type BankOfIrelandForm } from '../../../../queries/schema/carFinance.schema';

const BANK_OF_IRELAND = 'Bank of Ireland';

const BANKS = [
  {
    label: 'Select your primary bank',
    value: '',
  },
  'AIB',
  'Bank of Ireland',
  'EBS',
  'KBC Bank Ireland',
  'Permanent TSB',
  'Ulster Bank Ireland',
];

const BankingInformation = () => {
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const { control, resetField } = useFormContext<BankOfIrelandForm>();

  // Account details are shown only when the bank is Bank of Ireland
  const handleBankNameChange = (selected: string) => {
    if (selected !== BANK_OF_IRELAND) {
      resetField('accountDetails');
      setShowAccountDetails(false);
    } else {
      setShowAccountDetails(true);
    }
  };

  return (
    <CollapsiblePanelProvider panelName="bankingDetails">
      <CollapsiblePanel
        headerText="Banking Information"
        accordionName="Banking Information"
        mb={['S8', 'M16']}
        panelName="bankingDetails"
      >
        <Spacer marginBottom={['M16', 'M24']} />
        <Label htmlFor="bankingDetails.income">Net Monthly Income*</Label>
        <Controller
          control={control}
          name="bankingDetails.income"
          render={({ field, fieldState }) => (
            <FinancePriceField {...field} {...fieldState} />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="bankingDetails.monthlyMortgageRent">
          Monthly Rent Payments*
        </Label>
        <Controller
          control={control}
          name="bankingDetails.monthlyMortgageRent"
          render={({ field, fieldState }) => (
            <FinancePriceField {...field} {...fieldState} />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="bankingDetails.bankName">
          Please select your bank*
        </Label>
        <Controller
          control={control}
          name="bankingDetails.bankName"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              onChange={(selected) => {
                field.onChange(selected);
                handleBankNameChange(selected);
              }}
              placeholderAsOption
              options={BANKS}
            />
          )}
        />
        {showAccountDetails && (
          <>
            <Spacer marginBottom="S12" />
            <Divider />
            <Spacer marginBottom="M32" />
            <Label htmlFor="accountDetails.sortCode">Sort Code*</Label>
            <Controller
              control={control}
              name="accountDetails.sortCode"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter sort code"
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="accountDetails.accountNumber">
              Account Number*
            </Label>
            <Controller
              control={control}
              name="accountDetails.accountNumber"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter account number"
                />
              )}
            />
          </>
        )}
      </CollapsiblePanel>
    </CollapsiblePanelProvider>
  );
};

export { BankingInformation };
